/**
 * KtsMount — Kinetic Telemetry Service camera mount component.
 *
 * Phase 2: Real-time rep counting via MediaPipe Pose + sensor fusion.
 *
 * Flow:
 *   1. Opens camera + connects WebSocket to KTS
 *   2. Loads MediaPipe PoseLandmarker (lazy WASM download)
 *   3. Runs pose estimation at ~30fps
 *   4. HeuristicEngine evaluates joint angles → detects reps
 *   5. Each rep emits a bitblock (with joint_data + device_telemetry) to KTS
 *   6. Video is recorded simultaneously
 *   7. On finish: uploads video → seals SmartBlock
 */

import { useState, useRef, useEffect, useCallback, useMemo } from 'react';
import { KtsClient, type KtsMessage, type BitblockAck } from '@/lib/kts-client';
import { HeuristicEngine, type HeuristicRule } from '@/lib/heuristic-engine';
import { useMediaPipePose } from '@/hooks/useMediaPipePose';
import { useDeviceMotion } from '@/hooks/useDeviceMotion';
import { useVideoRecorder } from '@/hooks/useVideoRecorder';
import { uploadKtsVideo } from '@/lib/kts-upload';
import { useAuth } from '@/lib/auth-context';
import { angleBetweenPoints, LANDMARK, type NormalizedLandmark } from '@/lib/mediapipe-pose';
import { supabase } from '@/supabase/client';

const KTS_API = import.meta.env.VITE_KTS_URL || '';

interface KtsMountProps {
  sessionId: string;
  wsUrl: string;
  token: string;
  /** Tool id (e.g. "prospect.combine_pushup.v1"). Drives pipeline-class
   *  detection — rep_counter / timed_hold / explosive_timer. If absent,
   *  defaults to rep_counter behavior. */
  toolId?: string;
  heuristicRule?: HeuristicRule;
  onComplete: (result: SealResult) => void;
}

interface SealResult {
  verified_reps: number;
  merkle_root: string;
  /** @deprecated Removed when seal flow moved to pgdag-py. Use receipt_id. */
  snapchore_hash?: string;
  sealed_at: string;
  smartblock_id: string | null;
  video_url?: string;
  // ─── New fields surfaced by the pgdag-py bridge (post-Phase-2 seal). ───
  /** Per-step receipt id from pgdag-py merkle_seal proof. */
  receipt_id?: string | null;
  /** Adjudicator model that produced the verdict (e.g. "claude-opus-4-5"). */
  adjudicator_model?: string | null;
  /** Coach-readable session notes from the adjudicator. */
  notes?: string;
  /** Number of events the adjudicator rejected (too-fast, missing-up, etc). */
  rejected_event_count?: number;
  /** pgdag run id for traceability. */
  pgdag_run_id?: string;
  /** RunResult.status — typically "COMPLETE" on success. */
  pgdag_status?: string;
}

type Phase = 'loading' | 'connecting' | 'ready' | 'recording' | 'sealing' | 'done' | 'error';

const DEFAULT_RULE: HeuristicRule = {
  target_metric: 'repetition',
  joint_angles: ['elbow'],
  success_threshold_degrees: '< 110',  // More forgiving — full depth pushup is ~90, this catches partial reps too
  min_confidence: 0.3,                 // Lower threshold for initial testing
};

import { detectPipelineClass, getPipelineClassConfig } from '@/lib/kinetic-pipeline';

export default function KtsMount({ sessionId, wsUrl, token, toolId, heuristicRule, onComplete }: KtsMountProps) {
  const pipelineClass = detectPipelineClass(toolId);
  const pipelineConfig = getPipelineClassConfig(pipelineClass);
  const { user } = useAuth();
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const ktsRef = useRef<KtsClient | null>(null);
  const engineRef = useRef<HeuristicEngine | null>(null);
  const motionRef = useRef<{ accelerometer: { x: number; y: number; z: number }; gyroscope: { x: number; y: number; z: number } } | null>(null);

  const [phase, setPhase] = useState<Phase>('loading');
  const [repCount, setRepCount] = useState(0);
  const [confidence, setConfidence] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [cameraReady, setCameraReady] = useState(false);
  const [cameraStream, setCameraStream] = useState<MediaStream | null>(null);
  const [uploadProgress, setUploadProgress] = useState<string | null>(null);
  const [wsConnected, setWsConnected] = useState(false);
  const [elapsed, setElapsed] = useState(0);
  const [debugAngles, setDebugAngles] = useState<Record<string, number>>({});
  const [repPhase, setRepPhase] = useState<'waiting' | 'down'>('waiting');

  // Memoize rule to prevent engine re-creation on every render
  const rule = useMemo(
    () => heuristicRule || DEFAULT_RULE,
    [heuristicRule?.target_metric, heuristicRule?.success_threshold_degrees, heuristicRule?.min_confidence],
  );

  // Initialize heuristic engine (stable reference via memoized rule)
  useEffect(() => {
    engineRef.current = new HeuristicEngine(rule);
    return () => { engineRef.current?.reset(); };
  }, [rule]);

  // Device motion (sensor fusion)
  const { motion, available: motionAvailable, permitted: motionPermitted, requestPermission } = useDeviceMotion(phase === 'recording');

  // Keep motion ref in sync (avoid re-render on every sensor reading)
  useEffect(() => {
    if (motion) motionRef.current = motion;
  }, [motion]);

  // Video recorder — uses state so it re-initializes when camera stream is available
  const { recording: isRecordingVideo, start: startVideoRecording, stop: stopVideoRecording } = useVideoRecorder({ stream: cameraStream });

  // Session timer — independent of video recorder
  useEffect(() => {
    if (phase !== 'recording') { setElapsed(0); return; }
    const interval = setInterval(() => setElapsed((e) => e + 1), 1000);
    return () => clearInterval(interval);
  }, [phase]);

  // Connect to WebSocket
  useEffect(() => {
    const client = new KtsClient();
    ktsRef.current = client;

    const unsubscribe = client.onMessage((msg: KtsMessage) => {
      if ('ack' in msg && msg.ack) {
        setRepCount((msg as BitblockAck).verified_reps);
      }
      if ('type' in msg && msg.type === 'connected') {
        setWsConnected(true);
      }
      if ('error' in msg) {
        console.warn('[KtsMount] Server error:', msg.error);
      }
    });

    client.connect(wsUrl, token).catch((err) => {
      setError(`Connection failed: ${err.message}`);
      setPhase('error');
    });

    return () => {
      unsubscribe();
      client.close();
    };
  }, [wsUrl, token]);

  // Prevent accidental page close/navigation during active session
  useEffect(() => {
    if (phase !== 'recording' && phase !== 'sealing') return;

    const handler = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = 'You have an active recording session. Are you sure you want to leave?';
      return e.returnValue;
    };

    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [phase]);

  // Handle tab visibility changes — keep WebSocket alive
  useEffect(() => {
    if (phase !== 'recording') return;

    const handler = () => {
      if (document.visibilityState === 'hidden') {
        // Tab went to background — send heartbeat to keep connection alive
        ktsRef.current?.sendBitblock({
          event: 'heartbeat',
          timestamp_ms: Date.now(),
          confidence_score: 0,
          device_telemetry: {},
        });
      }
      if (document.visibilityState === 'visible' && ktsRef.current && !ktsRef.current.connected) {
        // Tab returned and WS dropped — attempt reconnect
        console.warn('[KtsMount] Tab returned, WebSocket disconnected. Attempting reconnect...');
        ktsRef.current.connect(wsUrl, token).then(() => {
          setWsConnected(true);
        }).catch((err) => {
          console.error('[KtsMount] Reconnect failed', err);
          setError('Connection lost. Please finish and try again.');
          setPhase('error');
        });
      }
    };

    document.addEventListener('visibilitychange', handler);
    return () => document.removeEventListener('visibilitychange', handler);
  }, [phase, wsUrl, token]);

  // Start camera
  useEffect(() => {
    let stream: MediaStream | null = null;

    async function startCamera() {
      try {
        stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'user', width: { ideal: 1280 }, height: { ideal: 720 } },
          audio: false,
        });
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          setCameraStream(stream);
          setCameraReady(true);
        }
      } catch (err) {
        console.error('[KtsMount] Camera access denied', err);
        setError('Camera access denied. Please allow camera permissions.');
        setPhase('error');
      }
    }

    startCamera();

    return () => {
      if (stream) {
        stream.getTracks().forEach((t) => t.stop());
      }
    };
  }, []);

  // MediaPipe pose estimation — only runs during recording
  const frameCountRef = useRef(0);
  const onPoseFrame = useCallback((landmarks: NormalizedLandmark[]) => {
    if (phase !== 'recording' || !engineRef.current || !ktsRef.current?.connected) return;

    const repEvent = engineRef.current.evaluate(landmarks);

    // Update rep phase indicator
    if (engineRef.current) {
      setRepPhase(engineRef.current.currentPhase);
    }

    // Show live joint angles every 10 frames (~3x per second)
    frameCountRef.current++;
    if (frameCountRef.current % 10 === 0) {
      const leftElbow = angleBetweenPoints(
        landmarks[LANDMARK.LEFT_SHOULDER], landmarks[LANDMARK.LEFT_ELBOW], landmarks[LANDMARK.LEFT_WRIST]
      );
      const rightElbow = angleBetweenPoints(
        landmarks[LANDMARK.RIGHT_SHOULDER], landmarks[LANDMARK.RIGHT_ELBOW], landmarks[LANDMARK.RIGHT_WRIST]
      );
      setDebugAngles({ L: Math.round(leftElbow), R: Math.round(rightElbow), avg: Math.round((leftElbow + rightElbow) / 2) });
    }

    if (repEvent) {
      // Rep detected! Send bitblock with full joint data + sensor fusion
      ktsRef.current.sendBitblock({
        event: 'rep_completed',
        timestamp_ms: repEvent.timestamp_ms,
        confidence_score: repEvent.confidence_score,
        device_telemetry: motionRef.current || {},
        joint_data: repEvent.joint_data,
      });
      setConfidence(repEvent.confidence_score);
    }

    // Draw pose skeleton overlay
    drawSkeleton(landmarks);
  }, [phase]);

  const { loading: poseLoading, ready: poseReady, error: poseError } = useMediaPipePose({
    videoRef,
    enabled: phase === 'recording' || phase === 'ready',
    onFrame: onPoseFrame,
  });

  // Phase transition: ready as soon as camera + WebSocket are up
  // MediaPipe loads in background — auto-enhances when available
  useEffect(() => {
    if (phase === 'recording' || phase === 'sealing' || phase === 'done') return;
    if (cameraReady && wsConnected) {
      setPhase('ready');
    }
  }, [cameraReady, wsConnected, phase]);

  // Draw skeleton overlay on canvas
  const drawSkeleton = useCallback((landmarks: NormalizedLandmark[]) => {
    const canvas = canvasRef.current;
    const video = videoRef.current;
    if (!canvas || !video) return;

    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    canvas.width = video.videoWidth || 640;
    canvas.height = video.videoHeight || 480;
    ctx.clearRect(0, 0, canvas.width, canvas.height);

    // Draw connections (simplified — major body lines)
    const connections = [
      [11, 13], [13, 15], // left arm
      [12, 14], [14, 16], // right arm
      [11, 12],           // shoulders
      [11, 23], [12, 24], // torso
      [23, 24],           // hips
      [23, 25], [25, 27], // left leg
      [24, 26], [26, 28], // right leg
    ];

    ctx.strokeStyle = '#00ff88';
    ctx.lineWidth = 3;

    for (const [a, b] of connections) {
      const la = landmarks[a];
      const lb = landmarks[b];
      if (!la || !lb || (la.visibility ?? 0) < 0.3 || (lb.visibility ?? 0) < 0.3) continue;

      ctx.beginPath();
      ctx.moveTo(la.x * canvas.width, la.y * canvas.height);
      ctx.lineTo(lb.x * canvas.width, lb.y * canvas.height);
      ctx.stroke();
    }

    // Draw key joints as dots
    ctx.fillStyle = '#00ff88';
    for (const lm of landmarks) {
      if ((lm.visibility ?? 0) < 0.3) continue;
      ctx.beginPath();
      ctx.arc(lm.x * canvas.width, lm.y * canvas.height, 4, 0, Math.PI * 2);
      ctx.fill();
    }
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    if (!ktsRef.current?.connected) return;

    // Request device motion permission (iOS requires user gesture)
    if (motionAvailable && !motionPermitted) {
      await requestPermission();
    }

    // Send session_start bitblock
    ktsRef.current.sendBitblock({
      event: 'session_start',
      timestamp_ms: Date.now(),
      confidence_score: 1.0,
      device_telemetry: motionRef.current || {},
    });

    // Start video recording
    startVideoRecording();

    engineRef.current?.reset();
    setRepCount(0);
    setPhase('recording');
  }, [motionAvailable, motionPermitted, requestPermission, startVideoRecording]);

  // Finish and seal
  const finishAndSeal = useCallback(async () => {
    if (!ktsRef.current) return;

    // Send session_end bitblock
    ktsRef.current.sendBitblock({
      event: 'session_end',
      timestamp_ms: Date.now(),
      confidence_score: 1.0,
      device_telemetry: motionRef.current || {},
    });

    setPhase('sealing');

    try {
      // Stop video recording and upload
      let videoUrl: string | undefined;
      const videoBlob = await stopVideoRecording();

      if (videoBlob && videoBlob.size > 0 && user?.id) {
        setUploadProgress('Uploading video...');
        try {
          videoUrl = await uploadKtsVideo(videoBlob, user.id, sessionId);
        } catch (uploadErr) {
          console.warn('[KtsMount] Video upload failed (non-fatal)', uploadErr);
          // Non-fatal — seal the SmartBlock even without video
        }
      }

      // Seal the session
      setUploadProgress('Sealing SmartBlock...');
      const res = await fetch(`${KTS_API}/api/sessions/${sessionId}/seal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ video_url: videoUrl }),
      });

      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(body?.error || `Seal failed: ${res.status}`);
      }

      const { data } = await res.json();

      // Save video to prospect media (so it shows in Media tab)
      if (videoUrl && user?.id) {
        try {
          // Get prospect_id from prospects table
          const { data: prospect, error: prospectErr } = await supabase
            .from('prospects')
            .select('id')
            .eq('owner_id', user.id)
            .maybeSingle();

          console.log('[KtsMount] prospect lookup', { prospectId: prospect?.id, prospectErr: prospectErr?.message, userId: user.id });

          if (prospect?.id) {
            const { error: insertErr } = await supabase.from('prospect_updates').insert({
              prospect_id: prospect.id,
              media_url: videoUrl,
              caption: `KTS Benchmark — ${data.verified_reps ?? repCount} reps (${new Date().toLocaleDateString()})`,
              approved: false, // pending coach review
            });

            if (insertErr) {
              console.error('[KtsMount] prospect_updates insert failed', insertErr.message, insertErr.details);
            } else {
              console.log('[KtsMount] prospect_updates saved', { prospectId: prospect.id, mediaUrl: videoUrl });
            }
          } else {
            console.warn('[KtsMount] No prospect found for user, skipping media save');
          }
        } catch (mediaErr) {
          console.warn('[KtsMount] Failed to save to media tab (non-fatal)', mediaErr);
        }
      }

      setPhase('done');
      ktsRef.current.close();
      onComplete({ ...data, video_url: videoUrl });
    } catch (err) {
      setError(`Seal failed: ${(err as Error).message}`);
      setPhase('error');
    }
  }, [sessionId, token, onComplete, stopVideoRecording, user?.id]);

  // Error state
  if (phase === 'error') {
    return (
      <div className="flex flex-col items-center justify-center rounded-2xl bg-red-50 p-8 text-center">
        <p className="text-lg font-semibold text-red-600">Error</p>
        <p className="mt-2 text-sm text-red-500">{error || poseError}</p>
        <button
          type="button"
          onClick={() => window.location.reload()}
          className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center gap-4 rounded-2xl bg-gray-900 p-4">
      {/* Camera preview with skeleton overlay */}
      <div className="relative w-full max-w-lg overflow-hidden rounded-xl bg-black">
        <video
          ref={videoRef}
          autoPlay
          playsInline
          muted
          className="w-full"
          style={{ transform: 'scaleX(-1)' }}
        />
        {/* Skeleton overlay canvas */}
        <canvas
          ref={canvasRef}
          className="absolute inset-0 w-full h-full pointer-events-none"
          style={{ transform: 'scaleX(-1)' }}
        />

        {/* Rep counter + phase lights overlay */}
        {phase === 'recording' && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 flex items-center gap-4 rounded-2xl bg-black/75 px-5 py-3">
            {/* Phase indicator lights */}
            {poseReady && (
              <div className="flex flex-col items-center gap-1.5">
                {/* DOWN light — green when bottom position reached */}
                <div className="flex items-center gap-1.5">
                  <div
                    className={`h-3.5 w-3.5 rounded-full border border-white/20 transition-colors duration-200 ${
                      repPhase === 'down' ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-gray-600'
                    }`}
                  />
                  <span className="text-[9px] font-medium text-white/50 w-8">DOWN</span>
                </div>
                {/* UP light — flashes green when rep completes (waiting = just returned up) */}
                <div className="flex items-center gap-1.5">
                  <div
                    className={`h-3.5 w-3.5 rounded-full border border-white/20 transition-colors duration-200 ${
                      repPhase === 'waiting' && repCount > 0 ? 'bg-green-500 shadow-[0_0_8px_rgba(34,197,94,0.6)]' : 'bg-gray-600'
                    }`}
                  />
                  <span className="text-[9px] font-medium text-white/50 w-8">UP</span>
                </div>
              </div>
            )}

            {/* Rep count */}
            <div className="text-center">
              <span className="text-4xl font-black text-white tabular-nums">{repCount}</span>
              <span className="ml-1 text-sm text-white/50">reps</span>
            </div>

            {/* Confidence */}
            {confidence > 0 && (
              <div className="text-center border-l border-white/20 pl-3">
                <span className="text-lg font-bold text-green-400">{Math.round(confidence * 100)}%</span>
                <p className="text-[10px] text-white/40">conf</p>
              </div>
            )}
          </div>
        )}

        {/* Debug: live joint angles */}
        {phase === 'recording' && poseReady && Object.keys(debugAngles).length > 0 && (
          <div className="absolute top-4 left-4 rounded-lg bg-black/60 px-2 py-1 text-[10px] font-mono text-green-400">
            {Object.entries(debugAngles).map(([k, v]) => (
              <div key={k}>{k}: {v}°</div>
            ))}
            <div className="text-yellow-400 mt-0.5">threshold: {rule.success_threshold_degrees}</div>
          </div>
        )}

        {/* Timer overlay */}
        {phase === 'recording' && (
          <div className="absolute top-4 right-4 flex items-center gap-2 rounded-full bg-black/50 px-3 py-1.5">
            <div className="h-2 w-2 animate-pulse rounded-full bg-red-500" />
            <span className="text-xs font-mono text-white/80">{formatDuration(elapsed)}</span>
          </div>
        )}

        {/* Loading overlay */}
        {phase === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/60">
            <div className="text-center text-white">
              <div className="mb-2 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent mx-auto" />
              <p className="text-sm">{poseLoading ? 'Loading pose model...' : 'Connecting...'}</p>
            </div>
          </div>
        )}

        {/* Connecting overlay */}
        {phase === 'connecting' && (
          <div className="absolute inset-0 flex items-center justify-center bg-black/50">
            <div className="text-center text-white">
              <div className="mb-2 h-8 w-8 animate-spin rounded-full border-2 border-white border-t-transparent mx-auto" />
              <p className="text-sm">Connecting to KTS...</p>
            </div>
          </div>
        )}
      </div>

      {/* Controls */}
      <div className="flex items-center gap-3">
        {phase === 'ready' && cameraReady && (
          <button
            type="button"
            onClick={startRecording}
            className="rounded-xl bg-green-600 px-8 py-4 text-lg font-bold text-white transition-transform hover:scale-105 active:scale-95"
          >
            Start
          </button>
        )}

        {phase === 'recording' && (
          <>
            {/* Manual rep button — shown when MediaPipe isn't ready yet */}
            {!poseReady && (
              <button
                type="button"
                onClick={() => {
                  if (ktsRef.current?.connected) {
                    ktsRef.current.sendRep(0.5); // lower confidence for manual counts
                  }
                }}
                className="rounded-xl bg-blue-600 px-8 py-4 text-lg font-bold text-white transition-transform active:scale-95"
              >
                Count Rep
              </button>
            )}

            <button
              type="button"
              onClick={finishAndSeal}
              className="rounded-xl bg-red-600 px-8 py-4 text-lg font-bold text-white transition-transform hover:scale-105 active:scale-95"
            >
              Finish
            </button>
          </>
        )}

        {phase === 'sealing' && (
          <div className="flex items-center gap-2 text-white">
            <div className="h-5 w-5 animate-spin rounded-full border-2 border-white border-t-transparent" />
            <span className="text-sm">{uploadProgress || 'Sealing SmartBlock...'}</span>
          </div>
        )}
      </div>

      {/* Status bar */}
      <div className="text-center text-xs text-gray-400">
        {phase === 'loading' && 'Connecting camera and session...'}
        {phase === 'ready' && 'Camera ready. Position yourself in frame and tap Start.'}
        {phase === 'recording' && !poseReady && 'Tap Count Rep manually. AI vision is loading in the background...'}
        {phase === 'recording' && poseReady && 'AI is tracking your movement. Reps are counted automatically.'}
        {phase === 'sealing' && 'Computing Merkle root and sealing your SmartBlock...'}

        {/* Sensor fusion indicator */}
        {phase === 'recording' && (
          <div className="mt-1 flex items-center justify-center gap-2 text-[10px]">
            <span className={poseReady ? 'text-green-400' : 'text-yellow-400'}>
              {poseReady ? 'AI Vision active' : 'AI loading...'}
            </span>
            <span className="text-gray-600">|</span>
            <span className={motionPermitted ? 'text-green-400' : 'text-gray-500'}>
              {motionPermitted ? 'Sensors active' : 'No motion sensors'}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}

function formatDuration(seconds: number): string {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}:${s.toString().padStart(2, '0')}`;
}
